import { useRef } from 'react';
import { clamp, type Vec2 } from '../../core/math';

export interface XYPadProps {
  value: Vec2;
  onChange: (value: Vec2) => void;
  min: Vec2;
  max: Vec2;
  step?: number;
  defaultValue?: Vec2;
  square?: boolean;
  unit?: string;
}

/**
 * Two-axis pad for `vec2` parameters — a gravity direction, a wind vector, a warp bias.
 * Dragging both components at once conveys the combined result far better than two
 * sliders can, and the crosshair doubles as a readout.
 */
export function XYPad({ value, onChange, min, max, step = 0.01, defaultValue, square = true, unit }: XYPadProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const normX = (max.x - min.x) === 0 ? 0.5 : (value.x - min.x) / (max.x - min.x);
  const normY = (max.y - min.y) === 0 ? 0.5 : (value.y - min.y) / (max.y - min.y);

  const snap = (v: number): number => Number((Math.round(v / step) * step).toFixed(6));

  const setFromEvent = (e: React.PointerEvent<HTMLDivElement>, fine: boolean): void => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let tx = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    let ty = clamp((e.clientY - rect.top) / rect.height, 0, 1);
    if (fine) {
      // Shift pulls the target towards the current value, giving finer control.
      tx = normX + (tx - normX) * 0.15;
      ty = normY + (ty - normY) * 0.15;
    }
    onChange({ x: snap(min.x + tx * (max.x - min.x)), y: snap(min.y + ty * (max.y - min.y)) });
  };

  return (
    <div className="space-y-1">
      <div
        ref={ref}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          dragging.current = true;
          setFromEvent(e, e.shiftKey);
        }}
        onPointerMove={(e) => dragging.current && setFromEvent(e, e.shiftKey)}
        onPointerUp={() => {
          dragging.current = false;
        }}
        onPointerCancel={() => {
          dragging.current = false;
        }}
        onDoubleClick={() => defaultValue && onChange({ ...defaultValue })}
        className="relative w-full cursor-crosshair touch-none overflow-hidden rounded-md border border-ink-700 bg-ink-850"
        style={{ aspectRatio: square ? '1 / 1' : '2 / 1' }}
      >
        {/* Centre guides */}
        <div className="pointer-events-none absolute inset-x-0 top-1/2 h-px bg-ink-800" />
        <div className="pointer-events-none absolute inset-y-0 left-1/2 w-px bg-ink-800" />
        {/* Crosshair */}
        <div
          className="pointer-events-none absolute h-full w-px bg-accent-500/30"
          style={{ left: `${normX * 100}%` }}
        />
        <div
          className="pointer-events-none absolute h-px w-full bg-accent-500/30"
          style={{ top: `${normY * 100}%` }}
        />
        <div
          className="pointer-events-none absolute h-[11px] w-[11px] -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ink-950 bg-accent-500"
          style={{ left: `${normX * 100}%`, top: `${normY * 100}%` }}
        />
      </div>
      <div className="flex gap-1 font-mono text-[10px] text-ink-500">
        <span className="flex-1">
          X {value.x.toFixed(2)}
          {unit}
        </span>
        <span className="flex-1 text-right">
          Y {value.y.toFixed(2)}
          {unit}
        </span>
      </div>
    </div>
  );
}
